import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import { withSavePreference, withMeQuery } from 'legacy/src/components/util';
import { withRouter } from 'react-router';
import {
  HIDE_DIGITAL_ASSESSMENT_INFO_MODAL,
  ATTESTATION_SUBJECT,
} from 'legacy/src/constants/types';
import { savePreferenceSuccess as savePreferenceSuccessAction } from 'legacy/src/components/auth/actions';
import { findSubjectByMasterSubjectId } from 'legacy-src/components/learnosity/utils/helpers';
import {
  ModalText,
  LabelForCheckBoxHybridCourse,
  NonBBCourseParagraph,
  StudentNonBBCourseParagraph,
} from './texts';
import {
  getCourseType,
  shouldShowBluebookNote,
  isNonBBCourse,
} from './constants';

const DigitalAssessmentInfoModal = ({
  onClose,
  isStudent,
  onSubmit = () => {},
  savePreference,
  me,
  userSubjects,
  savePreferenceSuccess,
  router,
  subjectData,
  isImpersonating,
  meQuery,
  isAdminOrCoordinator,
}) => {
  const [agree, setAgree] = React.useState(false);
  const [dontShowAgain, setDontShowAgain] = React.useState(false);

  const { subject } = router.params;

  const masterSubjectId = parseInt(subject, 10);
  const courseType = getCourseType(masterSubjectId);

  const subjectInfo = isStudent
    ? subjectData
    : findSubjectByMasterSubjectId(userSubjects, subject);

  const getBluebookDownloadURL = isStudent
    ? 'https://bluebook.collegeboard.org/students'
    : 'https://bluebook.collegeboard.org';

  const bottomLeftContent = (
    <div>
      <input
        type="checkbox"
        id="dontShowAgain"
        name="dontShowAgain"
        className="mr-3"
        checked={dontShowAgain}
        onChange={(e) => setDontShowAgain(e.target.checked)}
      />
      <label htmlFor="dontShowAgain">
        Do not show this to me again{' '}
        {!isAdminOrCoordinator && 'for this subject'}
      </label>
    </div>
  );

  const saveUserPreference = () => {
    const newPreference = {
      preference: HIDE_DIGITAL_ASSESSMENT_INFO_MODAL,
      value: 'true',
      context: ATTESTATION_SUBJECT,
      contextId: subjectInfo.initId,
    };

    savePreference({
      variables: {
        userId: me.initId,
        ...newPreference,
      },
    }).then(() => {
      // Update Redux store with just the new preference
      meQuery.refetch().then(({ data }) => {
        savePreferenceSuccess(data.me);
      });
    });
  };

  const closeModalAndSubmit = () => {
    if (dontShowAgain && !isImpersonating) {
      saveUserPreference();
    }
    onClose();
    onSubmit();
  };

  if (isAdminOrCoordinator) {
    return (
      <ConfirmDialog
        title="Update to the AP Classroom Test-Taking Experience"
        showCloseButton={false}
        size="big"
        onClose={onClose}
        onSuccess={closeModalAndSubmit}
        aria={{
          'aria-live': 'polite',
        }}
        content={{
          btnPrimary: 'Continue',
          bottomLeftContent,
        }}
        isPrimaryBtnDisabled={!agree}
        alignItemsBottomContent="between"
      >
        <div className="flex flex-col gap-8">
          <p className="m-0">
            AP Classroom has updated the look and feel of its online test-taking
            experience and added features so educators and students can begin to
            familiarize themselves with the question-responding experience of
            Bluebook™, the digital AP Exam application. While not all subjects
            will have their exams administered in Bluebook, the testing
            experience in AP Classroom has been updated for all subjects so
            students have a consistent testing experience in AP Classroom.
          </p>
          <p className="m-0">
            We will also be updating the presentation of questions in AP
            Classroom to align more closely to Bluebook. For example, displaying
            more questions in a two-column layout. While this will take some
            time to implement, all questions in AP Classroom will continue to
            prepare students for the content and skills assessed on the exam.
          </p>
          <p className="m-0">
            When you get closer to the exam, educators and students can further
            familiarize themselves with the test-taking experience that will be
            available on test day, including seeing section instructions and the
            scheduled breaks, by{' '}
            <a href={getBluebookDownloadURL} target="_blank" rel="noreferrer">
              {' '}
              downloading Bluebook{' '}
            </a>{' '}
            and using the Test Preview for their subject.
          </p>
          <p className="m-0">
            You can{' '}
            <a
              href="https://cb.org/ap-digital-2025"
              target="_blank"
              rel="noreferrer"
            >
              learn more about the 2025 digital AP Exams
            </a>{' '}
            on AP Central.
          </p>
          <div className="flex items-center bg-[#F2F2F2] p-4 rounded-xl">
            <input
              type="checkbox"
              id="agree"
              name="agree"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mr-5"
            />
            <label for="agree" className="text-base">
              I understand that while AP Classroom practice assessments will
              prepare students for the experience of responding to digital AP
              Exam questions, the best representation of the exam experience on
              test day is Bluebook’s Test Preview.
            </label>
          </div>
          <p className="m-0">
            Note: not all Bluebook test-taking features will be represented in
            AP Classroom at the start of the school year.
          </p>
        </div>
      </ConfirmDialog>
    );
  }

  return (
    <ConfirmDialog
      title="Update to the AP Classroom Test-Taking Experience"
      showCloseButton={false}
      size="big"
      onClose={onClose}
      onSuccess={closeModalAndSubmit}
      aria={{
        'aria-live': 'polite',
      }}
      content={{
        btnPrimary: 'Continue',
        bottomLeftContent,
      }}
      isPrimaryBtnDisabled={!agree}
      alignItemsBottomContent="between"
    >
      <div className="flex flex-col gap-8">
        {isNonBBCourse(masterSubjectId) ? (
          isStudent ? (
            <StudentNonBBCourseParagraph />
          ) : (
            <NonBBCourseParagraph />
          )
        ) : (
          <ModalText
            isStudent={isStudent}
            courseType={courseType}
            getBluebookDownloadURL={getBluebookDownloadURL}
          />
        )}
        <p className="m-0">
          You can{' '}
          <a
            href="https://cb.org/ap-digital-2025"
            target="_blank"
            rel="noreferrer"
          >
            learn more about the 2025 digital AP Exams
          </a>{' '}
          on AP Central.
        </p>

        <div className="flex items-center bg-[#F2F2F2] p-4 rounded-xl">
          <input
            type="checkbox"
            id="agree"
            name="agree"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className="mr-5"
          />
          <label for="agree" className="text-base">
            <LabelForCheckBoxHybridCourse
              courseType={courseType}
              isStudent={isStudent}
            />
          </label>
        </div>
        {shouldShowBluebookNote(masterSubjectId) && (
          <p className="m-0">
            Note: not all Bluebook test-taking features will be represented in
            AP Classroom at the start of the school year.{' '}
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
};

export default compose(
  withRouter,
  connect(
    ({
      userData: { isStudent, me, isImpersonating },
      userSubjects,
      subject: { data: subjectData },
    }) => ({
      isStudent,
      me,
      userSubjects,
      subjectData,
      isImpersonating,
    }),
    { savePreferenceSuccess: savePreferenceSuccessAction },
  ),
  withSavePreference,
  withMeQuery,
)(DigitalAssessmentInfoModal);
